import * as React from 'react';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { ModalSize } from 'app/components/constants';
import { ButtonInline, ValidationLevel, ValidationProps } from 'ui';
import {
  ComplexValue, FavouriteDefinition,
  FavouriteReplaceDomainNodes
} from 'app/types-shared';
import { FilterReplacementsMapFilters } from 'app/components/filters/filter-replacements/map-filters';
import { FilterReplacementsRemoveFilters } from 'app/components/filters/filter-replacements/remove-filters';
import {
  getDefaultDomainNodes, getOptionsIdsForRemoveFilters, getReplaceDomainNodesValidation,
  hasReplaceDomainNodesChanged, mapDefaultReplaceDomainNodesPerType, removeDefaultReplaceDomainNode,
  validateReplaceDomainNodes
} from 'app/components/filters/filter-replacements/get-replace-domain-nodes';
import { FiltersTranslation } from 'app/area/filters/constants';
import { MapFilterType } from 'app/components/filters/filter-replacements/constants';
import { SpinLoader } from 'app/components/spin-loader';
import styles from './filter-replacements.scss';

export interface FilterReplacementsProps {
  workingDefinition: FavouriteDefinition;
  removeDomainNodeOptions?: ComplexValue<number>[];
  originalDomainNodeOptions?: ComplexValue<number>[];
  replacementDomainNodeOptions?: ComplexValue<number>[];
  fetchingUpdateReplaceDomainNodes?: boolean;
  fetchingExtendedReplaceDomainNodes?: boolean;
  translation?: FiltersTranslation;
  locale: string;
  updateReplaceDomainNodes: (transform: (definition: FavouriteDefinition) => FavouriteDefinition, onSuccess: () => void) => void;
  onUpdateFilterReplacements?: (
    definition: FavouriteDefinition,
    updateReplaceDomainNodes: (transform: (definition: FavouriteDefinition) => FavouriteDefinition, onSuccess: () => void) => void,
    onSuccess: () => void
  ) => void;
  onLoadExtendedReplaceDomainNodes?: (favouriteId: number, onSuccess: (replaceDomainNodes: FavouriteReplaceDomainNodes[]) => void) => void;
  onLoadOriginalDomainNodesFields: ({ optionsIds, query }: { optionsIds?: number[]; query?: string }) => void;
  onLoadReplaceDomainNodesFields: ({ domain, query }: { domain?: number; query?: string }) => void;
  onLoadRemoveNodesFields: ({ optionsIds, query }: { optionsIds?: number[]; query?: string }) => void;
}

export interface FilterReplacementsState {
  isPrompt: boolean;
  isOpen: boolean;
  initialReplaceDomainNodes?: FavouriteReplaceDomainNodes[];
  replaceDomainNodes: FavouriteReplaceDomainNodes[];
  validation?: ValidationProps[];
}

export class FilterReplacements extends React.PureComponent<FilterReplacementsProps, FilterReplacementsState> {
  public state: Readonly<FilterReplacementsState> = {
    isPrompt: false,
    isOpen: false,
    initialReplaceDomainNodes: [],
    replaceDomainNodes: [],
    validation: []
  };

  public componentDidMount(): void {
    if (!this.state.replaceDomainNodes?.length) {
      this.setState({
        initialReplaceDomainNodes: this.props.workingDefinition?.replaceDomainNodes || [],
        replaceDomainNodes: this.props.workingDefinition?.replaceDomainNodes || []
      });
    }
  }

  private onOpenModal = () => {
    this.setState({
      isOpen: true
    });

    if (!this.state.replaceDomainNodes?.length) {
      this.setState({
        initialReplaceDomainNodes: this.props.workingDefinition?.replaceDomainNodes || [],
        replaceDomainNodes: this.props.workingDefinition?.replaceDomainNodes || []
      });
    } else {
      this.props.onLoadExtendedReplaceDomainNodes(this.props.workingDefinition?.favourite?.v,
        (extendedReplaceDomainNodes: FavouriteReplaceDomainNodes[]) => {
          this.setState({
            initialReplaceDomainNodes: extendedReplaceDomainNodes || [],
            replaceDomainNodes: extendedReplaceDomainNodes || []
          });
        });
    }
  };

  private onCloseModal = () => {
    this.setState({
      isOpen: false,
      replaceDomainNodes: this.state.initialReplaceDomainNodes || []
    });
  };

  private onPrompt = () => {
    this.setState({
      isPrompt: !this.state.isPrompt
    });
  };

  private onClosePrompt = () => {
    this.setState({
      isPrompt: false,
      isOpen: false,
      validation: [],
      replaceDomainNodes: this.state.initialReplaceDomainNodes || []
    });
  };

  private onClose = () => {
    if (this.shouldPrompt()) {
      this.setState({
        isPrompt: true
      });
    } else {
      this.onCloseModal();
    }
  };

  private onSuccess = () => {
    this.setState({
      isOpen: false,
      validation: [],
      initialReplaceDomainNodes: this.state.replaceDomainNodes
    });
  };

  private handleUpdateFilterReplacements = () => {

    const validation = validateReplaceDomainNodes(getDefaultDomainNodes(this.state.replaceDomainNodes),
      this.props.translation.required_field);
    if (validation.some(item => item.level === ValidationLevel.error)) {
      this.setState({ validation });
      return;
    }

    this.props.onUpdateFilterReplacements({
      ...this.props.workingDefinition, replaceDomainNodes: this.state.replaceDomainNodes
    }, this.props.updateReplaceDomainNodes, this.onSuccess);
  };

  private updateReplaceDomainNodes = (replaceDomainNodes: FavouriteReplaceDomainNodes[]) => {
    this.setState({
      replaceDomainNodes
    });
  };

  private shouldPrompt = () => {
    return hasReplaceDomainNodesChanged(
      this.state.initialReplaceDomainNodes,
      this.state.replaceDomainNodes
    );
  };

  private onFieldChange = (index: number, type: MapFilterType) => {
    return (updatedNode: ComplexValue<number>) => {
      this.onChange(index, updatedNode, type);
    };
  };

  private onChange = (
    index: number,
    value: ComplexValue<number>,
    type: MapFilterType) => {

    const updatedReplaceDomainNodes = mapDefaultReplaceDomainNodesPerType(index, value, type, this.state.replaceDomainNodes);
    this.updateReplaceDomainNodes(updatedReplaceDomainNodes);
    this.props.onLoadRemoveNodesFields({ optionsIds: getOptionsIdsForRemoveFilters(updatedReplaceDomainNodes) });
    const newValidation = this.state.validation.filter(item => item.messages?.[0]?.parameters?.[type] !== index);
    this.setState({ validation: newValidation });
  };

  private removeReplaceDomainNode = (index: number) => {
    const replaceDomainNodes = removeDefaultReplaceDomainNode(index, this.state.replaceDomainNodes);
    this.updateReplaceDomainNodes(replaceDomainNodes);
    this.props.onLoadRemoveNodesFields({ optionsIds: getOptionsIdsForRemoveFilters(replaceDomainNodes) });
    this.setState({ validation: getReplaceDomainNodesValidation(index, this.state.validation) });
  };

  private renderModal = () => {
    const panelProps = {
      replaceDomainNodes: this.state.replaceDomainNodes,
      removeDomainNodeOptions: this.props.removeDomainNodeOptions,
      originalDomainNodeOptions: this.props.originalDomainNodeOptions,
      replacementDomainNodeOptions: this.props.replacementDomainNodeOptions,

      fetchingExtendedReplaceDomainNodes: this.props.fetchingExtendedReplaceDomainNodes,

      validation: this.state.validation,
      translation: this.props.translation,
      locale: this.props.locale,

      onFieldChange: this.onFieldChange,
      removeReplaceDomainNode: this.removeReplaceDomainNode,
      onLoadReplaceDomainNodesFields: this.props.onLoadReplaceDomainNodesFields,
      onLoadOriginalDomainNodesFields: this.props.onLoadOriginalDomainNodesFields,
      onLoadRemoveNodesFields: this.props.onLoadRemoveNodesFields,
      updateReplaceDomainNodes: this.updateReplaceDomainNodes
    };

    return <ModalWrapper
      headerTitle={this.props.translation.filter_replacements_title}
      btnTitle={this.props.translation.done}
      btnCancelTitle={this.props.translation.cancel}
      canCloseOnBackground={false}
      closeOnDone={false}
      isDisabled={!this.shouldPrompt() || this.props.fetchingUpdateReplaceDomainNodes || this.props.fetchingExtendedReplaceDomainNodes}
      onClose={this.onClose}
      onDone={this.handleUpdateFilterReplacements}
      size={ModalSize.xtraLarge}>
      {this.props.fetchingUpdateReplaceDomainNodes
        && <div className={styles.spinLoaderContainer}>
          <SpinLoader />
        </div>}
      <FilterReplacementsMapFilters {...panelProps} />
      <FilterReplacementsRemoveFilters {...panelProps} />
      {this.state.isPrompt && this.confirmationModal()}
    </ModalWrapper>;
  };

  private confirmationModal = () => {
    return <div className={styles.modalPrompt}>
      <ModalWrapper
        headerTitle={this.props.translation.filter_replacements_cancel_changes}
        btnTitle={this.props.translation.filter_replacements_undo_changes}
        btnCancelTitle={this.props.translation.filter_replacements_keep_editing}
        onClose={this.onPrompt}
        onDone={this.onClosePrompt}>
        {this.props.translation.filter_replacements_prompt_message}
      </ModalWrapper>
    </div>;
  };

  public render() {
    return (
      <div>
        <ButtonInline onClick={this.onOpenModal}>
          {`${this.props.translation.filter_replacements_title} 
          (${this.state.replaceDomainNodes.length})`}
        </ButtonInline>
        {this.state.isOpen && this.renderModal()}
      </div>
    );
  }
}
